/* =============================================================================
   VIEWS / ADMIN / KDS — Vollbild-Küchen-Tablet-View (Phase 15, Session 19)
   =============================================================================
   Eigene Top-Level-View neben customer/admin/cancel. Optimiert für ein
   stationäres Tablet in der Küche. Zeigt alle aktiven Bestellungen
   (pending + confirmed + preparing + ready) als große Kacheln, color-coded
   nach Wartezeit, mit jeweils einem prominenten Next-Status-Button.

   Aufgerufen über:
     - Hash '#kds' (per view-switch.js → switchView('kds'))
     - Cmd/Ctrl+Shift+K (in bindAdminShortcut registriert)

   Authentifizierung:
     - Nur eingeloggte User dürfen rein. Andernfalls bleiben wir auf
       view-admin (Login-Form).

   Realtime:
     - Wir nutzen das App.events 'order:realtime'-Event, das die orders-Tab
       schon abonniert. KDS-spezifisch:
         * INSERT pending → eigener Ping (KDS-Sound, anderer Ton)
         * UPDATE/DELETE → einfach Render-Refresh
     - Damit kein Doppel-Ping passiert, setzt KDS body.classList 'kds-active',
       und die Cockpit-Notification skippt den Sound, wenn 'kds-active' an ist.

   Color-Coding (Wartezeit ab order.createdAt):
     - grün:  < 5 Min
     - gelb:  5–15 Min
     - rot:   > 15 Min

   Exporte:
     renderKds()             — Haupt-Renderer für #view-kds
     enterKdsView() / exitKdsView()
     kdsRefresh()            — Re-Render bei Realtime-Events
     kdsNextRoundStatus(orderId, round) — Next-Button je Runden-Kachel (S139 P5)
============================================================================ */

import { htmlEscape, showToast, formatCustomizationsByCategory, dateToISO } from '../../utils/helpers.js';
import { getDisplayConfig, isDisplayFieldVisible, colorForOrder, computeOrderTimer, isDrinkItem, kdsCategoryAllowsBuild } from '../../core/display-config.js';
import { renderOperatorBadges } from './operator-ui.js';
import {
  getCachedItems, fetchItemsFor, batchFetchItemsFor, invalidateItems, groupItemsByRound,
  hasCachedRoundStatus, hasPendingRoundStatus, fetchRoundStatusFor, batchFetchRoundStatusFor,
  invalidateRoundStatus, roundChannelStatus
} from '../../core/order-items-cache.js';
import { ALLERGENS_EU14 } from '../../core/nutrition.js'; // S152 A2 — Allergen-Labels für On-Demand-Nährwerte
// S253 — Küchen-Hinweise der Gäste (phase_185). Werden bei der Anzeige gelesen,
// nie an der Runde eingefroren: eine spätere Änderung gilt rückwirkend.
import { guestNotes, notesForRound, renderGuestNotesHtml, initGuestNotes } from './table-guest-notes.js';

// S152 A2 — Allergen-Key → deutsches Label (für die aufklappbare Nährwert-Ansicht).
const ALLERGEN_LABEL = Object.fromEntries(ALLERGENS_EU14.map(a => [a.key, a.label]));
// Kacheln (tileKey = order__round), bei denen die Nährwert-/Allergen-Ansicht
// aufgeklappt ist. Modul-Zustand → überlebt die häufigen KDS-Re-Renders.
const _nutriExpanded = new Set();
// S154 — Kacheln mit aufgeklappter Bauanleitung (gleiche on-demand-Mechanik).
const _buildExpanded = new Set();
// S253 — Abmelder des Hinweis-Abos (nur einmal verdrahten).
let _kdsNotesOff = null;

// S154 — nummerierte Bauanleitung (add grün „+", remove durchgestrichen). Gemeinsame
// Optik für KDS; Theke nutzt eine eigene, gleich aufgebaute Variante in bar.js.
function renderBuildHtml(steps) {
  if (!steps || !steps.length) {
    return `<div class="kds-build-empty hint" style="font-size:.72rem; margin-top:2px;">Keine Bauanleitung hinterlegt.</div>`;
  }
  // S154b/c — Anzeige als echter Burger-Querschnitt: Deckel (top) oben, Boden
  // (bottom) unten. Die Schritt-Daten bleiben in Aufbau-Reihenfolge (bottom→top);
  // nur die Darstellung wird gespiegelt.
  return `<div class="kds-build" style="margin:4px 0 0; font-size:.74rem; color:#555;">${steps.slice().reverse().map(buildStepRow).join('')}</div>`;
}

// S154b — eine Bauanleitungs-Zeile als Stapel-Schicht. Bun (bottom/top) bündig als
// Boden/Deckel (getönt, abgerundet); Füllung (middle) eingerückt → wie ein Burger-
// Querschnitt von unten nach oben. add grün „+", remove durchgestrichen.
function buildStepRow(s) {
  const bun = s.layer === 'bottom' || s.layer === 'top';
  const indent = (s.layer === 'middle') ? 'margin-left:18px;' : '';
  const bunStyle = bun ? 'background:#f0e2c8; border-radius:12px; font-weight:600; padding:2px 10px;' : 'padding:1px 8px;';
  const kindStyle = s.kind === 'remove' ? 'text-decoration:line-through; color:#b94a48;'
                  : s.kind === 'add' ? 'color:#2f6b3a; font-weight:600;' : '';
  const st = s.station ? ` <span class="hint">· ${htmlEscape(s.station)}</span>` : '';
  return `<div style="${indent}${bunStyle}${kindStyle} margin-top:2px;">${htmlEscape(s.text)}${st}</div>`;
}

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { orders: [], settings: {} };
}

// -----------------------------------------------------------------------------
// Items-Cache — S104-B: gemeinsames Item-Access-Modul (core/order-items-cache.js).
// Ersetzt den KDS-eigenen `_itemsCache`/Batch-Loader. EINE Quelle für alle
// Displays; Batch gruppiert die flache `listItemsForOrderIds`-Liste jetzt korrekt
// nach orderId (vorher tot durch falsche `itemsByOrder[id]`-Annahme).
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// Status-Pipeline (gleiche wie in orders.js — bewusst dupliziert, weil KDS
// auch in offline-Skelettzustand laden kann)
// -----------------------------------------------------------------------------
// Master-Status-Filter: KDS interessiert alles non-terminal. Sub-Status
// (kitchen_status) entscheidet, ob die Order noch Arbeit verlangt.
const KDS_MASTER_STATUSES = ['pending', 'confirmed', 'preparing', 'ready'];

const STATUS_LABEL = {
  pending:    'Neu',
  confirmed:  'Angenommen',
  preparing:  'In Zubereitung',
  ready:      'Bereit',
  picked_up:  'Abgeholt',
  cancelled:  'Storniert',
  no_show:    'No-Show'
};

// S139 P5 — Channel-Action-Logik jetzt PRO RUNDE (Runde × Kanal=kitchen).
// `roundStatus` ist der Küchen-Status DIESER Runde ('pending'|'preparing'|
// 'ready'|undefined). Jede Runde hat ihren eigenen Button und ist unabhängig
// fortschaltbar; Vorrunden werden nicht angefasst.
//   - Master pending (Web-Order noch nicht angenommen) → Annehmen (Master-Aktion).
//   - sonst Runden-Küchen-Status: pending→Zubereiten, preparing→Bereit, ready→kein Button.
export function nextKitchenAction(order, roundStatus) {
  if (!order) return null;
  const m = order.status;
  if (m === 'cancelled' || m === 'picked_up') return null;
  if (m === 'pending') {
    return { kind: 'master', status: 'confirmed', label: '✓ Annehmen' };
  }
  const k = roundStatus || 'pending';
  if (k === 'pending')   return { kind: 'channel', channel: 'kitchen', status: 'preparing', label: '▶ Zubereiten' };
  if (k === 'preparing') return { kind: 'channel', channel: 'kitchen', status: 'ready',     label: '🍔 Bereit' };
  // Phase 19.4: KDS löst KEIN 'picked_up' aus (Cockpit-Aktion). Runde ready →
  // Kachel bleibt mit „Bereit" sichtbar, kein weiterer Button.
  if (k === 'ready') return null;
  return null;
}

// Label der Runden-Kachel — zeigt den Küchen-Status DIESER Runde. Wenn die Runde
// noch pending ist, aber der Master schon angenommen wurde → „Angenommen".
function kdsStatusLabel(order, roundStatus) {
  const k = roundStatus || 'pending';
  if (k === 'pending' && order.status && order.status !== 'pending') {
    return STATUS_LABEL.confirmed;
  }
  return STATUS_LABEL[k] || k;
}

// S139 P5 — Counter-Bucket EINER Runden-Kachel (Pure-Helper, testbar). Buckets:
//   pending   = Master noch nicht angenommen
//   confirmed = angenommen, Runde noch nicht begonnen (round kitchen=pending)
//   preparing = Runde in Zubereitung · ready = Runde fertig
export function kdsCountBucket(masterStatus, roundStatus) {
  const k = roundStatus || 'pending';
  if (k === 'preparing') return 'preparing';
  if (k === 'ready')     return 'ready';
  if (masterStatus === 'pending') return 'pending';
  return 'confirmed';
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function minutesSince(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return 0;
  const diffMs = Date.now() - t;
  return Math.max(0, Math.floor(diffMs / 60000));
}

// waitColor wird jetzt dynamisch über display-config gesteuert (Phase 16.4).
// Die alte Fallback-Funktion bleibt erhalten für Aufrufer, die noch keine
// Order kennen — sie nutzt das KDS-Default (5/10 Min Schwellen).
function waitColor(mins) {
  if (mins < 5)  return 'wait-green';
  if (mins < 10) return 'wait-yellow';
  return 'wait-red';
}

function fmtTime(t) {
  return String(t || '').slice(0, 5);
}

// -----------------------------------------------------------------------------
// UX-Win S24: Heute-Filter (Default an). Verhindert, dass Morgen-Vorbestellungen
// schon heute auf dem KDS auftauchen. Toggle in der Topbar zeigt "Alle aktiven
// Orders" — falls der Operator doch alles sehen will. Modul-State (session-only).
// -----------------------------------------------------------------------------
let _kdsShowAllDates = false;

export function kdsToggleDateFilter() {
  _kdsShowAllDates = !_kdsShowAllDates;
  renderKds();
}

// S60 FIX.3 — Pre-Order-Window: 60 Min vor pickupTime wird die Order live
// (lila Badge), zum eingestellten Zeitpunkt scharf. Vergangenheits-Orders
// (pickupTime schon erreicht) sind sowieso aktiv. Datumsvergleiche entfallen,
// damit Mitternacht/UTC-Slice keine Orders mehr schluckt.
const PREORDER_WINDOW_MS = 60 * 60 * 1000;

/** Parst pickupDate (YYYY-MM-DD) + pickupTime (HH:MM) zu lokalen ms.
 *  Lokal weil Restaurant in seiner TZ arbeitet. */
function pickupAtMs(o) {
  if (!o || !o.pickupDate) return null;
  const [y, m, d] = String(o.pickupDate).split('-').map(Number);
  const [h, mi]   = String(o.pickupTime || '00:00').split(':').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, h || 0, mi || 0, 0, 0).getTime();
}

/** Pre-Order = pickupTime liegt in der Zukunft. Wird im Tile als lila Badge
 *  visualisiert (siehe Tile-Renderer). Wird beim Scharf-Werden automatisch
 *  zur normalen Order, weil pickupAtMs <= now. */
export function kdsIsPreOrder(o) {
  const ms = pickupAtMs(o);
  if (ms === null) return false;
  return ms > Date.now();
}

function activeOrders() {
  // Phase 19: Master darf nicht cancelled/picked_up sein. KDS interessiert
  // alles in pending/confirmed/preparing/ready (Master), unabhängig vom
  // kitchen-Sub-Status — Pure-Drink-Orders fliegen ohnehin durch den
  // Empty-Filter im renderKds-Loop raus.
  let orders = (getState().orders || []).filter(o => KDS_MASTER_STATUSES.includes(o.status));

  // S60 FIX.3 — Zeitfenster-Filter (Jonas-Spec): Pre-Orders sind 60 Min vor
  // pickupTime sichtbar (lila), dann scharf zum Termin. „Alle"-Toggle hebt
  // den Cutoff auf und zeigt auch weiter entfernte Vorbestellungen.
  if (!_kdsShowAllDates) {
    const cutoff = Date.now() + PREORDER_WINDOW_MS;
    const today = dateToISO(new Date());
    orders = orders.filter(o => {
      const ms = pickupAtMs(o);
      // Ohne pickupDate (z.B. ad-hoc Drinks ohne Termin) immer zeigen.
      if (ms === null) return true;
      // S100-B — Vortags-Pipeline raus: KDS ist heute-fokussiert, die offenen
      // Altlasten fängt jetzt die Carry-Over-Sektion im operativen Tab (S100-A).
      // Ohne dieses Netz wäre das Datensatz-Verlust — daher nur zusammen mit S100-A.
      if (o.pickupDate < today) return false;
      // Vergangenheit (heute) + 60-Min-Fenster sichtbar; weiter in der Zukunft aus.
      return ms <= cutoff;
    });
  }
  // Sortierung umgestellt im Funktionstest Audit-S21 (Jonas-Spec):
  // "Von rechts gesehen muss immer das angezeigt werden was als nächstes
  //  raus muss. Reihenfolge darf sich nur ändern wenn etwas Priorität
  //  bekommt oder vom Bestellungszeitraum zwischen zwei orders plaziert wird."
  //
  // Sortier-Strategie:
  //   1. Primär: pickup_time ABSTEIGEND → späteste Abholung links, dringendste
  //      Abholung rechts (CSS-Grid füllt von links nach rechts).
  //   2. Sekundär (nur bei IDENTISCHER pickup_time): Status-Rank — wer schon
  //      weiter in der Pipeline ist (preparing → ready) rückt nach rechts,
  //      sodass der Bediener die "fast fertige" Order rechts findet.
  //   3. Tertiär: createdAt ABSTEIGEND als Tiebreaker — älteste Order rechts.
  //
  // Wichtig: Status ist nur Tiebreaker innerhalb DERSELBEN pickup_time —
  // bei unterschiedlichen pickup_times bleibt die Position auch bei
  // Status-Übergang stabil (kein Verrutschen, das den Bediener verwirrt).
  const STATUS_RANK_FOR_TIEBREAK = { pending: 0, confirmed: 1, preparing: 2, ready: 3 };
  return orders.slice().sort((a, b) => {
    // 1. pickup_time absteigend (späteste zuerst = links)
    const aKey = String(a.pickupDate || '') + ' ' + String(a.pickupTime || '00:00');
    const bKey = String(b.pickupDate || '') + ' ' + String(b.pickupTime || '00:00');
    if (aKey !== bKey) return bKey.localeCompare(aKey);
    // 2. Status-Rank aufsteigend (weiter = höhere Zahl = rechts)
    const r = (STATUS_RANK_FOR_TIEBREAK[a.status] || 0) - (STATUS_RANK_FOR_TIEBREAK[b.status] || 0);
    if (r !== 0) return r;
    // 3. createdAt absteigend (älteste = rechts)
    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  });
}

// -----------------------------------------------------------------------------
// Tile-Renderer
// -----------------------------------------------------------------------------

/**
 * Findet die Kategorie eines Items via Menu-Lookup im State (settings.menuItems
 * oder state.menuItems). Fallback auf eine Heuristik: name enthält "Burger"
 * → 'Burger', "Bowl" → 'Bowls', "Salat" → 'Salate'.
 */
function detectCategory(item) {
  // 1. Direkt am Item — categorySnapshot ist die DB-Variante (aus order_items),
  //    category das inline-Feld, menuCategory ein Admin-Path-Fallback.
  if (item.categorySnapshot) return String(item.categorySnapshot);
  if (item.category_snapshot) return String(item.category_snapshot);
  if (item.category) return String(item.category);
  if (item.menuCategory) return String(item.menuCategory);
  // 2. Menu-Item-Lookup via menuItemId — _state.menu (nicht .menuItems)
  const state = getState();
  const menu = (state.menu || state.menuItems || (state.settings && state.settings.menuItems) || []);
  if (Array.isArray(menu)) {
    const mi = menu.find(m => m.id === item.menuItemId || m.id === item.menu_item_id);
    if (mi && mi.category) return String(mi.category);
  }
  // 3. Heuristik auf den Namen
  const name = String(item.name || item.nameSnapshot || item.name_snapshot || item.title || '').toLowerCase();
  if (name.includes('burger')) return 'Burger';
  if (name.includes('bowl'))   return 'Bowls';
  if (name.includes('salat'))  return 'Salate';
  return '';
}

/**
 * Rendert die Info-Felder (orderNumber, waitTime, customer, pickup, status, note)
 * in der per Settings konfigurierten Reihenfolge — alle Sichtbarkeits-Toggles
 * werden respektiert. Wird sowohl vom KDS als auch vom Theken-Display genutzt
 * (Bar-Tile-Renderer ruft denselben Helper, mit display='bar').
 */
export function renderConfiguredFields(display, fieldsState) {
  const cfg = getDisplayConfig(display);
  // fieldsState: { orderNumber, waitTime, customer, pickup, status, note } → fertig
  //              gerenderte HTML-Strings pro Feld. Wir picken in der Reihenfolge
  //              aus der Settings-Liste und überspringen unsichtbare.
  return cfg.fields
    .filter(f => f.visible && fieldsState[f.id])
    .map(f => fieldsState[f.id])
    .join('');
}

function renderTile(o, round, foodItems, roundStatusRow, multiRound) {
  // S139 P5 — eine Kachel je (Order, Runde-mit-Speisen). Status/Timer/Button
  // beziehen sich auf DIESE Runde. Für Timer/Ampel bauen wir eine
  // Runden-Sicht der Order: Küchen-Status + Phasen-Timestamps + Startzeit
  // (createdAt) der Runde überschreiben die Order-Felder — so läuft der Timer
  // jeder Runde ab IHRER Anlage/Phase (computeOrderTimer/colorForOrder lesen
  // kitchenStatus + kitchen*At + createdAt unverändert).
  const rs = roundStatusRow || null;
  const kStatus = rs ? rs.status : (Number(round) === 1 ? (o.kitchenStatus || 'pending') : 'pending');
  const tileOrder = {
    ...o,
    kitchenStatus:      kStatus,
    kitchenPreparingAt: rs ? rs.preparingAt : o.kitchenPreparingAt,
    kitchenReadyAt:     rs ? rs.readyAt : o.kitchenReadyAt,
    createdAt:          (rs && rs.createdAt) || o.createdAt
  };
  const timer  = computeOrderTimer('kds', tileOrder);
  const colCls = colorForOrder('kds', tileOrder, timer.mins, timer.mode);
  const id     = String(o.id || '');
  const next   = nextKitchenAction(o, kStatus);
  const tileKey = id + '__' + round;

  // S152 A2 — On-Demand-Nährwerte: nur wenn Flag an UND diese Kachel aufgeklappt.
  const ingFlagOn = !!(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb
                        && window.App && window.App.nutritionCache);
  const showNutri = ingFlagOn && _nutriExpanded.has(tileKey);
  // S196 (Jonas) — Positions-Kommentar nur zeigen, wenn das Display-Feld „Notiz"
  // sichtbar ist (System → Displays). Order-Ebenen-„Bemerkung" bleibt getrennt.
  const showItemNote = isDisplayFieldVisible('kds', 'itemNote');

  // Items kommen vorgefiltert vom Haupt-Renderer (renderKds): entweder `null`
  // (Skeleton, Items noch nicht geladen) oder die Speisen-Items DIESER Runde.
  const items = foodItems;

  let itemsHtml;
  if (items === null) {
    itemsHtml = `<li class="kds-item-loading">⏳ Lade Positionen…</li>`;
  } else {
    const renderLi = (it, idx) => {
      const qty  = Number(it.qty || it.quantity || 1);
      const name = htmlEscape(it.name || it.nameSnapshot || it.name_snapshot || it.title || 'Position');
      const cat  = detectCategory(it);
      // S154 — Bauanleitung öffnet sich per Klick auf die Position selbst (kein
      // extra Toggle). Zustand je Item-Key, überlebt Re-Render.
      const itemKey = String(it.id || (tileKey + ':' + idx));
      // S171-Hotfix — Hammer (🔨/Bauanleitung) nach KOMPOSIERBARER KATEGORIE gaten
      // (Burger/Bowls/Salate), NICHT nach buildStepsSync: der A2-Cache ist im KDS
      // oft nicht geladen (→ null) und hätte den Hammer von ALLEN Mains entfernt.
      // Beilage/Getränk/Optionen → kein Hammer. Deal-Hauptgericht = normales
      // order_item → gleiche Regel. Die Steps werden erst beim Aufklappen geholt
      // (renderBuildHtml zeigt bei leeren Steps sauber „Keine Bauanleitung …").
      const canBuild = ingFlagOn && kdsCategoryAllowsBuild(cat);
      const showBuild = canBuild && _buildExpanded.has(itemKey);
      // Kategorie-spezifische Sortierung (Burger/Bowls/Salate) — sonst Default.
      let cust = '';
      if (it.customizations) {
        const lines = formatCustomizationsByCategory(it.customizations, cat);
        if (lines && lines.length) {
          cust = lines.map(line => `<div class="kds-item-cust-line">${htmlEscape(line)}</div>`).join('');
        }
      }
      // S152 A2 — komponierte Nährwerte NUR auf Abruf (Flag ingredientsDb +
      // Kachel-Toggle „🥗 Nährwerte"). Die Küche sieht standardmäßig keine kcal
      // (Lärm beim Bauen); wer die Zusammenstellung anschaut, klappt sie auf.
      let nutri = '';
      if (showNutri) {
        const mid = it.menuItemId || it.menu_item_id;
        let n = null;
        try { n = window.App.nutritionCache.composeLineSync(mid, it.customizations || {}); } catch (_) {}
        if (n) {
          const allerg = (n.allergens || []).map(a => htmlEscape(ALLERGEN_LABEL[a] || a)).join(', ');
          nutri = `<div class="kds-item-nutri" style="font-size:.72rem; color:#8a8a8a; margin-top:2px;">
              ${n.energyKcal} kcal · F ${n.fatG} · KH ${n.carbsG} · E ${n.proteinG} g${n.missingData ? ' · ⚠️ unvollständig' : ''}${allerg ? `<br>Allergene: ${allerg}` : ''}
            </div>`;
        }
      }
      // S154 — Bauanleitung (Belege-Reihenfolge) je Position aus dem A2-Cache.
      let build = '';
      if (showBuild) {
        const mid = it.menuItemId || it.menu_item_id;
        let steps = null;
        try { steps = window.App.nutritionCache.buildStepsSync(mid, it.customizations || {}); } catch (_) {}
        build = renderBuildHtml(steps);
      }
      // Klick auf die Position toggelt die Bauanleitung — nur an baubaren Mains.
      const clickAttr = canBuild
        ? ` onclick="kdsToggleBuild('${htmlEscape(itemKey)}')" style="cursor:pointer;" title="${showBuild ? 'Bauanleitung ausblenden' : '🔨 Bauanleitung anzeigen'}"`
        : '';
      // S196 Cut b1 — Positions-Kommentar (order_items.notes) aus dem Warenkorb
      // sichtbar machen (Küche liest die Sonderwünsche pro Gericht).
      const noteRaw = it.notes != null ? it.notes : it.note;
      const noteHtml = (showItemNote && noteRaw && String(noteRaw).trim())
        ? `<div class="kds-item-note">📝 ${htmlEscape(String(noteRaw).trim())}</div>`
        : '';
      return `
        <li class="kds-item${showBuild ? ' kds-item-building' : ''}"${clickAttr}>
          <span class="kds-item-qty">${qty}×</span>
          <span class="kds-item-name">${name}</span>${canBuild ? ' <span class="hint" style="font-size:.66rem;">🔨</span>' : ''}
          ${cust ? `<div class="kds-item-cust">${cust}</div>` : ''}
          ${noteHtml}
          ${nutri}
          ${build}
        </li>
      `;
    };
    // S139 P5 — KEIN Dimmen mehr. Diese Kachel zeigt nur die Items IHRER Runde.
    // Bei mehreren Runden ein Batch-Header „Runde N" zur Orientierung.
    const header = (multiRound && round != null) ? `<li class="kds-round-header">Runde ${round}</li>` : '';
    itemsHtml = header + items.map((it, idx) => renderLi(it, idx)).join('');
  }

  const orderNum = htmlEscape(o.orderNumber || o.order_number || ('#' + id.slice(0, 6)));
  const customer = htmlEscape(o.customerName || o.customer_name || '—');
  const pickup   = fmtTime(o.pickupTime || o.pickup_time);
  const note     = (o.notes || o.special_requests) ? htmlEscape(o.notes || o.special_requests) : '';

  // service_type-Badge (Session 20 / F2d, S61 erweitert um delivery):
  //   "Hier" bei dine-in mit Tischname, "To-Go" bei takeaway, "🛵 Lieferung"
  //   bei delivery + Adress-Block für den Fahrer/Kommissionierung.
  const svcType = o.serviceType || 'takeaway';
  const isDineIn   = svcType === 'dine-in';
  const isDelivery = svcType === 'delivery';
  const tableName = (() => {
    if (!isDineIn || !o.tableId) return '';
    const state = getState();
    const t = (state.tables || []).find(x => x.id === o.tableId);
    return t ? (t.name || o.tableId) : o.tableId;
  })();
  let svcBadge;
  if (isDineIn) {
    svcBadge = `<span class="kds-svc-badge kds-svc-dine-in">🍽️ Tisch ${htmlEscape(tableName)}</span>`;
  } else if (isDelivery) {
    // S61 — Lieferung-Badge mit Zonen-Hinweis (Liefergebiet) als Kontext für
    // die Küche. Fallback auf city, falls keine zone_name in der Adresse.
    // Volle Adresse + Fahrer-Zuweisung kommt ins Liefer-Cockpit (S62).
    // S62 HF10 — wenn der Disponent „Zuweisen" geklickt hat (delivery_status
    // ∈ {assigned, out_for_delivery}), wird der Badge zu „🛵 FAHRER WARTET"
    // umgeschaltet — prominentes Signal für die Küche, dass jetzt produziert
    // werden muss, weil der Fahrer am Pass steht.
    const da = o.deliveryAddress || o.delivery_address || null;
    const zoneHint = da
      ? (da.zone_name ? ` · ${htmlEscape(da.zone_name)}` : (da.city ? ` · ${htmlEscape(da.city)}` : ''))
      : '';
    const driverWaiting = o.deliveryStatus === 'assigned' || o.deliveryStatus === 'out_for_delivery';
    svcBadge = driverWaiting
      ? `<span class="kds-svc-badge kds-svc-delivery kds-svc-driver-waiting" title="Fahrer ist zugewiesen und wartet — Priorität!">🛵 FAHRER WARTET${zoneHint}</span>`
      : `<span class="kds-svc-badge kds-svc-delivery">🛵 Lieferung${zoneHint}</span>`;
  } else {
    svcBadge = `<span class="kds-svc-badge kds-svc-takeaway">🥡 To-Go</span>`;
  }

  // Phase 17.4 (Session 22) — Reservierungs-Vorbestellung-Badge.
  // Macht für die Küche auf einen Blick erkennbar, dass diese Order aus einer
  // bestätigten Reservierung kommt (nicht spontan-Walk-In). pickup_time ist
  // dann auch die geplante Service-Zeit. Quelle entweder via source='reservation'
  // (Migration 60) oder Backward-Compat über reservation_id IS NOT NULL.
  const isReservation = o.source === 'reservation' || !!o.reservationId;
  const reservationBadge = isReservation
    ? `<span class="kds-svc-badge kds-svc-reservation" title="Aus Reservierung übernommen">📅 Vorbestellung · ${htmlEscape(pickup || '—')}</span>`
    : '';

  // S60 FIX.3 — Pre-Order-Badge: jede Order deren pickupTime noch in der
  // Zukunft liegt (auch Walk-In-Vorbestellungen, nicht nur Reservierungen).
  // Im 60-Min-Fenster vor Service sichtbar (lila), danach automatisch scharf.
  const preOrderMs = pickupAtMs(o);
  const isPreOrder = preOrderMs !== null && preOrderMs > Date.now();
  const preOrderMinsAhead = isPreOrder ? Math.max(0, Math.round((preOrderMs - Date.now()) / 60000)) : 0;
  const preOrderBadge = isPreOrder && !isReservation
    ? `<span class="kds-svc-badge kds-svc-preorder" title="Wird in ${preOrderMinsAhead} Min scharf">🟣 Pre-Order · in ${preOrderMinsAhead} Min</span>`
    : '';

  // S61 — Lieferadresse NICHT mehr im KDS rendern. Volle Adresse + Fahrer-
  // Zuweisung gehört in das geplante Liefer-Cockpit (S62, separate View
  // parallel zu KDS/Theke). Die Küche bekommt nur den Stadt-Hinweis im
  // Service-Badge oben.

  // Pro Feld den fertigen HTML-Block — getrennte Header/Body/Note-Zonen, damit
  // der visuelle Aufbau intakt bleibt. Die Settings entscheiden Reihenfolge und
  // Sichtbarkeit über renderConfiguredFields().
  // Phase 18: waitTime nutzt jetzt timer.label statt nackt "X min".
  // Bei mode='waiting' steht da z.B. "Service in 270 min" mit lila Card-Color.
  const pickupLabel = isDineIn ? 'Tisch' : (isDelivery ? 'Lieferung' : 'Abholung');
  const pickupValue = isDineIn ? htmlEscape(tableName) : (pickup || '—');
  // S129 — Operator-Attribution-Badges (Single-Source aus operator-ui).
  const operatorBadges = renderOperatorBadges(o);
  const fieldHtml = {
    orderNumber: `<div class="kds-tile-num">${orderNum} ${svcBadge}${reservationBadge}${preOrderBadge}${operatorBadges}</div>`,
    waitTime:    `<div class="kds-tile-wait">${htmlEscape(timer.label)}</div>`,
    customer:    `<div class="kds-tile-line"><strong>${customer}</strong></div>`,
    pickup:      `<div class="kds-tile-line">${pickupLabel}: <strong>${pickupValue}</strong></div>`,
    status:      `<div class="kds-tile-status">${kdsStatusLabel(o, kStatus)}</div>`,
    note:        note ? `<div class="kds-note">📝 ${note}</div>` : ''
  };

  // Header = orderNumber + waitTime (sofern visible), Body = customer/pickup/status,
  // Note = note. Wir lassen den Renderer ALLE Felder in einer Reihenfolge mischen —
  // visuell sortieren die Cards das schon richtig, weil sie nacheinander stehen.
  const orderedHtml = renderConfiguredFields('kds', fieldHtml);

  const preOrderCls = isPreOrder ? 'kds-tile-preorder' : '';
  // S139 P5 — data-tile = order__round (eindeutig je Runden-Kachel; data-id für
  // Realtime-Bezug erhalten). Button setzt den Status DIESER Runde.
  // S253 — die Hinweise der Gäste, die IN DIESER RUNDE etwas bestellt haben.
  // Nur am Tisch: eine Abhol-Bestellung hat keine Belegung, an der ein Hinweis
  // hängen könnte. Gelesen wird bei jedem Render — ändert der Gast den Text
  // nach dem Absenden, steht hier beim nächsten Zeichnen der neue Stand.
  const guestNoteHtml = (isDineIn && o.occupancyId)
    ? renderGuestNotesHtml(notesForRound(guestNotes(), o.occupancyId, items || []), { cls: 'kds' })
    : '';

  return `
    <div class="kds-tile ${colCls} ${preOrderCls}" data-id="${htmlEscape(id)}" data-tile="${htmlEscape(tileKey)}">
      <div class="kds-tile-fields">${orderedHtml}</div>
      ${guestNoteHtml}
      <ul class="kds-items">${itemsHtml}</ul>
      ${ingFlagOn ? `
        <button class="kds-nutri-toggle" style="background:none; border:none; color:#8a8a8a; font-size:.74rem; cursor:pointer; padding:2px 4px;"
                onclick="kdsToggleNutri('${htmlEscape(tileKey)}')"
                title="Nährwerte &amp; Allergene je Position ein-/ausblenden">
          ${showNutri ? '▴ Nährwerte ausblenden' : '🥗 Nährwerte & Allergene'}
        </button>
      ` : ''}
      ${next ? `
        <button class="kds-next-btn" onclick="kdsNextRoundStatus('${htmlEscape(id)}', ${round})">
          ${next.label}
        </button>
      ` : ''}
    </div>
  `;
}

// -----------------------------------------------------------------------------
// Haupt-Renderer
// -----------------------------------------------------------------------------

export function renderKds() {
  const root = document.getElementById('view-kds');
  if (!root) return;

  const orders = activeOrders();

  // S139 P5 — eine Kachel je (Order, Runde-mit-Speisen). Items pro Order laden,
  // nach Runde gruppieren, je Runde die Speisen filtern → eine Tile pro Runde
  // mit Speisen. Solange Items noch nicht geladen sind: optimistische Skeleton-
  // Tile (round=null) + async nachladen. Zusätzlich den Runden-Status-Cache
  // (per Runde×Kanal) befüllen — fehlt er, default auf den order-eben Aggregat-
  // Status (transient bis der Fetch auflöst).
  const tilesData = [];
  for (const o of orders) {
    const id = String(o.id || '');
    // Runden-Status der Order sicherstellen (async, gleiche Robustheit wie Items)
    if (!hasCachedRoundStatus(id) && !hasPendingRoundStatus(id)) {
      fetchRoundStatusFor(id).then(() => {
        if (document.body.classList.contains('kds-active')) renderKds();
      }).catch(err => console.warn('[kds] renderKds after fetchRoundStatusFor failed:', err));
    }
    const inlineItems = Array.isArray(o.items) ? o.items : null;
    const cached = getCachedItems(id);
    const rawItems = (inlineItems && inlineItems.length) ? inlineItems : (cached || null);
    if (rawItems === null) {
      // Items noch nicht geladen → optimistic Skeleton-Tile, async nachladen
      fetchItemsFor(id).then(() => {
        if (document.body.classList.contains('kds-active')) renderKds();
      }).catch(err => console.warn('[kds] renderKds after fetchItemsFor failed:', err));
      tilesData.push({ order: o, round: null, foodItems: null, roundStatus: null, multiRound: false });
      continue;
    }
    // Nach Runde gruppieren, je Runde die Speisen-Items behalten.
    const foodGroups = groupItemsByRound(rawItems)
      .map(g => ({ round: g.round, items: g.items.filter(it => !isDrinkItem(it)) }))
      .filter(g => g.items.length > 0);
    if (foodGroups.length === 0) continue; // Pure-Drink-Order → nur Theke
    const multiRound = foodGroups.length > 1;
    // S196-Fix (Jonas) — Runden absteigend expandieren (neueste links, ÄLTESTE
    // rechts), konsistent mit sortKdsOrders („älteste = rechts"). Vorher landete
    // Runde 1 (die älteste) links → Widerspruch zur Board-Konvention.
    for (const g of foodGroups.slice().reverse()) {
      tilesData.push({
        order: o, round: g.round, foodItems: g.items,
        roundStatus: roundChannelStatus(id, g.round, 'kitchen'), multiRound
      });
    }
  }

  // Counter zählen jetzt pro RUNDEN-KACHEL nach deren Küchen-Status (jede Runde
  // = eine Arbeitseinheit). Skeleton-Tiles (round=null) zählen nicht.
  //   pending    = Master noch nicht angenommen (order.status=pending)
  //   confirmed  = angenommen, Runde noch nicht begonnen (round kitchen=pending)
  //   preparing  = Runde in Zubereitung · ready = Runde fertig
  const counts = tilesData.reduce((acc, t) => {
    if (t.round === null) return acc;
    const o = t.order;
    const k = (t.roundStatus && t.roundStatus.status)
      || (Number(t.round) === 1 ? (o.kitchenStatus || 'pending') : 'pending');
    const bucket = kdsCountBucket(o.status, k);
    acc[bucket] = (acc[bucket] || 0) + 1;
    return acc;
  }, {});

  const tiles = tilesData.length === 0
    ? `<div class="kds-empty">
         <div class="kds-empty-icon">🍳</div>
         <div class="kds-empty-text">Keine offenen Bestellungen.</div>
         <div class="kds-empty-hint">Sobald eine Bestellung reinkommt, taucht sie hier auf.</div>
       </div>`
    : tilesData.map(t => renderTile(t.order, t.round, t.foodItems, t.roundStatus, t.multiRound)).join('');

  // S60 W4.12 — Walk-In-ohne-Order-Reminder: aktive Walk-Ins die >20 Min sitzen
  // und keine verknüpfte Order haben → Service-Reminder für die Küche.
  const state = getState();
  const stateOrdersWithOcc = new Set((state.orders || []).filter(o => o.occupancyId).map(o => o.occupancyId));
  const lonelyWalkIns = (state.tableOccupancies || []).filter(o => {
    if (o.deletedAt || o.status !== 'active' || !o.startedAt) return false;
    if (stateOrdersWithOcc.has(o.id)) return false;
    const elapsed = (Date.now() - new Date(o.startedAt).getTime()) / 60000;
    return elapsed >= 20;
  });
  const walkInBanner = lonelyWalkIns.length > 0 ? `
    <div class="kds-walkin-reminder" style="background:#fff4d6;color:#7a4a10;padding:8px 14px;border-bottom:2px solid #d97a2c;font-size:.92rem;display:flex;gap:14px;align-items:center;flex-wrap:wrap;">
      <strong>🪑 ${lonelyWalkIns.length} Walk-In${lonelyWalkIns.length>1?'s':''} ohne Bestellung &gt; 20 Min:</strong>
      ${lonelyWalkIns.slice(0, 5).map(o => {
        const t = (state.tables || []).find(t => t.id === o.tableId);
        const name = (t && t.name) || o.tableId || '?';
        const min = Math.round((Date.now() - new Date(o.startedAt).getTime()) / 60000);
        return `<span>Tisch ${htmlEscape(String(name))} · ${o.guests}P · ${min} Min</span>`;
      }).join(' · ')}
      ${lonelyWalkIns.length > 5 ? `<span>+ ${lonelyWalkIns.length - 5} weitere</span>` : ''}
    </div>` : '';

  root.innerHTML = `
    <div class="kds-topbar">
      <div class="kds-brand">
        <span class="kds-brand-mark">G&amp;G</span>
        <span>Küchen-Display</span>
      </div>
      <div class="kds-counts">
        <span class="kds-count-pill pending">Neu: ${counts.pending || 0}</span>
        <span class="kds-count-pill confirmed">Angenommen: ${counts.confirmed || 0}</span>
        <span class="kds-count-pill preparing">In Zubereitung: ${counts.preparing || 0}</span>
        <span class="kds-count-pill ready">Bereit: ${counts.ready || 0}</span>
      </div>
      <div class="kds-actions">
        <button class="btn-kds-ghost" onclick="kdsToggleDateFilter()"
                title="${_kdsShowAllDates ? 'Nur heute zeigen' : 'Alle aktiven Tage zeigen'}">
          ${_kdsShowAllDates ? '📅 Alle' : '📅 Heute'}
        </button>
        <button class="btn-kds-ghost" onclick="kdsToggleFullscreen()" title="Vollbild">⛶</button>
        <button class="btn-kds-ghost" onclick="switchView('admin')">← Cockpit</button>
      </div>
    </div>
    ${walkInBanner}
    <div class="kds-grid">${tiles}</div>
  `;
}

// -----------------------------------------------------------------------------
// Status-Wechsel (delegiert an orderSetStatus, der RPC + Event-Bus auslöst)
// -----------------------------------------------------------------------------

export async function kdsNextRoundStatus(id, round) {
  const order = (getState().orders || []).find(o => o.id === id);
  if (!order) return;
  const rs = roundChannelStatus(id, round, 'kitchen');
  const roundStatus = rs ? rs.status : (Number(round) === 1 ? (order.kitchenStatus || 'pending') : 'pending');
  const next = nextKitchenAction(order, roundStatus);
  if (!next) return;
  // Optimistic UI: genau DIESE Runden-Kachel markieren (data-tile = order__round)
  const tile = document.querySelector(`.kds-tile[data-tile="${CSS.escape(id + '__' + round)}"]`);
  if (tile) tile.classList.add('kds-tile-pending-action');
  try {
    if (next.kind === 'channel') {
      // S139 P5 — Status DIESER Runde × Kanal via set_round_channel_status.
      // Master + abgeleiteter kitchen/bar-Status werden im RPC aggregiert.
      const storage = window.App && window.App.storage && window.App.storage.orders;
      if (!storage || typeof storage.setRoundChannelStatus !== 'function') {
        throw new Error('Runden-Status-API nicht verfügbar');
      }
      const updated = await storage.setRoundChannelStatus(id, round, next.channel, next.status);
      invalidateRoundStatus(id); // lokaler Cache → nächster Render lädt frisch
      if (updated && window.App.events) {
        await window.App.events.emit('order:status_changed', {
          order: updated, channel: next.channel, channelStatus: next.status
        });
      } else {
        renderKds();
      }
    } else {
      // Master-Aktion (pending→confirmed = Annehmen). Bleibt order-eben.
      if (typeof window.orderSetStatus !== 'function') {
        throw new Error('Bestellungs-Modul nicht geladen');
      }
      await window.orderSetStatus(id, next.status);
    }
    // renderKds wird gleich über das 'order:status_changed'-Event neu gerendert.
  } catch (e) {
    if (tile) tile.classList.remove('kds-tile-pending-action');
    showToast('Status-Wechsel fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

// -----------------------------------------------------------------------------
// View-Lifecycle
// -----------------------------------------------------------------------------

let _kdsInitialized = false;
let _kdsTickerInterval = null;

export function enterKdsView() {
  document.body.classList.add('kds-active');
  renderKds();
  installKdsListenersOnce();
  // S253 — Hinweise laden und auf Änderungen hören. Ohne das Abo bliebe eine
  // nachträglich getippte Allergie unsichtbar, bis zufällig etwas anderes ein
  // Neuzeichnen auslöst — und „rückwirkend" ist hier ein Jonas-Entscheid, kein
  // Nebeneffekt.
  if (!_kdsNotesOff) {
    _kdsNotesOff = initGuestNotes(() => {
      if (document.body.classList.contains('kds-active')) renderKds();
    });
  }
  // Pre-Fetch: alle aktiven Order-Items vorab laden, damit die Skeleton-State
  // höchstens beim allerersten Render kurz aufblitzt.
  try {
    const orders = activeOrders();
    const orderIds = orders.map(o => String(o.id || '')).filter(Boolean);
    // S60 PERF.1 — Batch-Loader statt N+1 (1 Request statt N).
    batchFetchItemsFor(orderIds).then(() => {
      if (document.body.classList.contains('kds-active')) renderKds();
    }).catch(err => console.warn('[kds] batch pre-fetch failed:', err));
    // S139 P5 — Runden-Status im selben Schwung vorladen (eigene Tabelle).
    batchFetchRoundStatusFor(orderIds).then(() => {
      if (document.body.classList.contains('kds-active')) renderKds();
    }).catch(err => console.warn('[kds] round-status pre-fetch failed:', err));
    // S152 A2 — Nährwert-Cache wird NICHT eager geladen; der On-Demand-Toggle
    // („🥗 Nährwerte & Allergene" je Kachel) lädt ihn beim ersten Aufklappen.
  } catch (e) {
    console.warn('[kds] pre-fetch items failed:', e);
  }
  // Hash sauber auf '#kds' setzen — auch wenn schon '#admin' o.ä. davor stand.
  // history.replaceState verhindert einen zusätzlichen History-Eintrag.
  try {
    if (window.location.hash !== '#kds') {
      const url = window.location.pathname + window.location.search + '#kds';
      window.history.replaceState(null, '', url);
    }
  } catch (_) { /* manche Browser-Sandboxen blockieren replaceState */ }
  // Wartezeit-Ticker: alle 30s neu rendern, damit die Color-Codes mit der Zeit
  // weiterwandern. Das ist günstig, weil renderKds einfach das Root-DOM ersetzt.
  if (_kdsTickerInterval) clearInterval(_kdsTickerInterval);
  _kdsTickerInterval = setInterval(() => {
    if (document.body.classList.contains('kds-active')) renderKds();
  }, 30000);
}

export function exitKdsView() {
  document.body.classList.remove('kds-active');
  if (_kdsTickerInterval) {
    clearInterval(_kdsTickerInterval);
    _kdsTickerInterval = null;
  }
  // Hash aufräumen — den '#kds'-Teil entfernen, andere Hash-Fragmente
  // (z.B. '#admin') bleiben erhalten, damit das normale Routing nicht stört.
  try {
    if (window.location.hash.includes('#kds')) {
      const cleaned = window.location.hash.replace(/#kds/g, '');
      const url = window.location.pathname + window.location.search + cleaned;
      window.history.replaceState(null, '', url);
    }
  } catch (_) { /* sandboxed Browser */ }
}

function installKdsListenersOnce() {
  if (_kdsInitialized) return;
  _kdsInitialized = true;
  const App = window.App;
  if (!App || !App.events) return;

  // S60 PERF.3 — kdsScheduleRender: zentrales Debouncing für KDS-Re-Renders.
  // Alle Realtime-Events (sowohl Sub-Events als auch lokale order:*-Events)
  // laufen durch diesen Helper. Bei rapid-fire Events wird nur 1 Render pro
  // ~150ms-Fenster ausgeführt. Vermeidet die vorherige 2× Renders pro Event
  // sowie unnötige Vollständig-Rebuilds bei lokalen UI-Aktionen.
  let _kdsRenderTimer = null;
  function kdsScheduleRender() {
    if (!document.body.classList.contains('kds-active')) return;
    if (_kdsRenderTimer) return;
    _kdsRenderTimer = setTimeout(() => {
      _kdsRenderTimer = null;
      if (document.body.classList.contains('kds-active')) renderKds();
    }, 150);
  }

  App.events.on('order:realtime', payload => {
    if (!document.body.classList.contains('kds-active')) return;
    try {
      // Items- + Runden-Status-Cache der betroffenen Order selektiv invalidieren
      const newId = payload && payload.new && payload.new.id;
      const oldId = payload && payload.old && payload.old.id;
      if (newId) { invalidateItems(String(newId)); invalidateRoundStatus(String(newId)); }
      if (oldId && oldId !== newId) { invalidateItems(String(oldId)); invalidateRoundStatus(String(oldId)); }
      if (payload && payload.eventType === 'INSERT' && payload.new
          && payload.new.status === 'pending'
          && typeof window.playOrderPing === 'function') {
        window.playOrderPing();
      }
      // S60 PERF.3 — Items-Prefetch und Render in EINEM Flow (vorher: 2 Renders)
      if (newId) {
        fetchItemsFor(String(newId)).then(() => kdsScheduleRender())
          .catch(err => console.warn('[kds] realtime item refresh failed:', err));
      } else {
        kdsScheduleRender();
      }
    } catch (e) {
      console.warn('[kds] realtime handler:', e);
    }
  });
  App.events.on('order:status_changed', payload => {
    const o = payload && payload.order;
    if (o && o.id) { invalidateItems(String(o.id)); invalidateRoundStatus(String(o.id)); }
    kdsScheduleRender();
  });
  App.events.on('order:cancelled', payload => {
    const o = payload && payload.order;
    if (o && o.id) { invalidateItems(String(o.id)); invalidateRoundStatus(String(o.id)); }
    kdsScheduleRender();
  });
  // S139 P5 — Runden-Status-Realtime: schaltet ein anderes Gerät eine Runde
  // fort, invalidieren wir den Runden-Status-Cache der Order + laden frisch.
  App.events.on('order:round_status:realtime', payload => {
    if (!document.body.classList.contains('kds-active')) return;
    const row = (payload && (payload.new || payload.old)) || null;
    const oid = row && (row.order_id || row.orderId);
    if (!oid) { kdsScheduleRender(); return; }
    invalidateRoundStatus(String(oid));
    fetchRoundStatusFor(String(oid)).then(() => kdsScheduleRender())
      .catch(err => console.warn('[kds] round-status realtime refresh failed:', err));
  });
  // S62 HF10 — Liefer-Order „scharf": wenn der Disponent einen Fahrer
  // zugewiesen hat, hört das KDS hier mit und gibt einen Ping ab, damit die
  // Küche aufmerksam wird (Order ist jetzt priorisiert — Fahrer wartet).
  App.events.on('order:delivery_assigned', payload => {
    const o = payload && payload.order;
    if (!o || !document.body.classList.contains('kds-active')) return;
    if (o.id) invalidateItems(String(o.id));
    if (typeof window.playOrderPing === 'function') {
      try { window.playOrderPing(); } catch (_) {}
    }
    kdsScheduleRender();
  });
  App.events.on('order:created', payload => {
    if (payload && payload.id) {
      fetchItemsFor(String(payload.id)).then(() => kdsScheduleRender())
        .catch(err => console.warn('[kds] order:created item fetch failed:', err));
    } else {
      kdsScheduleRender();
    }
  });
}

// -----------------------------------------------------------------------------
// Fullscreen-Toggle (für Tablet-Wand-Mount: F11-ähnlich per Button)
// -----------------------------------------------------------------------------
export function kdsToggleFullscreen() {
  const doc = document.documentElement;
  if (!document.fullscreenElement) {
    if (doc.requestFullscreen) doc.requestFullscreen().catch(() => {});
  } else {
    if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
  }
}

// S152 A2 — Nährwert-/Allergen-Ansicht einer Kachel auf Abruf ein-/ausklappen.
// Zustand in _nutriExpanded (überlebt Re-Render); Cache bei Bedarf nachladen,
// dann neu rendern, damit die Werte synchron stehen.
export function kdsToggleNutri(tileKey) {
  if (!tileKey) return;
  if (_nutriExpanded.has(tileKey)) {
    _nutriExpanded.delete(tileKey);
    renderKds();
    return;
  }
  _nutriExpanded.add(tileKey);
  const cache = window.App && window.App.nutritionCache;
  if (cache && cache.ensureLoaded) {
    cache.ensureLoaded().then(() => renderKds()).catch(() => renderKds());
  } else {
    renderKds();
  }
}

// S154 — Bauanleitung einer Position per Klick aufs Gericht ein-/ausklappen (kein
// eigener Toggle-Button). Key = Item-Id; Cache lädt beim ersten Öffnen.
export function kdsToggleBuild(itemKey) {
  if (!itemKey) return;
  if (_buildExpanded.has(itemKey)) {
    _buildExpanded.delete(itemKey);
    renderKds();
    return;
  }
  _buildExpanded.add(itemKey);
  const cache = window.App && window.App.nutritionCache;
  if (cache && cache.ensureLoaded) {
    cache.ensureLoaded().then(() => renderKds()).catch(() => renderKds());
  } else {
    renderKds();
  }
}

// -----------------------------------------------------------------------------
// Backward-Compat
// -----------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderKds,
    enterKdsView,
    exitKdsView,
    kdsRefresh: renderKds,
    kdsNextRoundStatus,
    kdsToggleFullscreen,
    kdsToggleDateFilter,
    kdsToggleNutri,
    kdsToggleBuild
  });
}

// -----------------------------------------------------------------------------
// Test-Hooks (vitest) — Pure-Filter + Toggle-State setzen, ohne renderKds/DOM.
// -----------------------------------------------------------------------------
export function _setKdsShowAllDates(v) { _kdsShowAllDates = !!v; }
export { activeOrders as _kdsActiveOrders };
